import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import Header from "@/components/Header";
import UserWallet from "@/components/UserWallet";
import {
  TrendingUp,
  TrendingDown,
  Zap,
  Wallet,
  ShieldAlert,
  Clock,
  Info,
  Flame,
  Landmark,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtStxoshiCompact = (micro) => {
  const n = (Number(micro) || 0) / 1_000_000;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const MARKET_META = {
  BTC:     { fmt: (v) => `$${v?.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, dp: 2 },
  STX:     { fmt: (v) => `$${v?.toLocaleString(undefined, { maximumFractionDigits: 4 })}`, dp: 4 },
  STXOSHI: { fmt: (v) => `$${(v || 0).toExponential(4)}`, dp: 8 },
};

function fmtStxoshi(micro) {
  const v = (Number(micro) || 0) / 1_000_000;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtSigned(micro) {
  const v = (Number(micro) || 0) / 1_000_000;
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function relTime(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Perps() {
  const navigate = useNavigate();
  const { isConnected, getAuthHeaders } = useWallet();
  const { showUsd, stxoshiUsd } = useCurrency();

  // USD-aware display helpers (shadow the module-level ones inside this
  // component) so the global STX/USD switch flips every perps figure.
  const _rate = stxoshiUsd || 0.0000037;
  const _usd = (micro) => ((Number(micro) || 0) / 1_000_000) * _rate;
  const _fmtUsd = (u) => {
    const a = Math.abs(u);
    const d = a >= 1 ? 2 : a >= 0.01 ? 4 : a > 0 ? 8 : 2;
    return `$${u.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: d })}`;
  };
  const unitLabel = showUsd ? "USD" : "STXOSHI";
  const fmtStxoshi = (micro) =>
    showUsd ? _fmtUsd(_usd(micro)) : ((Number(micro) || 0) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const fmtSigned = (micro) => {
    const v = (Number(micro) || 0) / 1_000_000;
    const sign = v >= 0 ? "+" : "";
    if (showUsd) return `${v < 0 ? "-" : sign}${_fmtUsd(Math.abs(_usd(micro)))}`;
    return `${sign}${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };
  const fmtStxoshiCompact = (micro) => {
    if (showUsd) return _fmtUsd(_usd(micro));
    const n = (Number(micro) || 0) / 1_000_000;
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState("BTC");
  const [side, setSide] = useState("long");
  const [notionalStx, setNotionalStx] = useState("100"); // STXOSHI (whole, not micro)
  const [leverage, setLeverage] = useState(5);
  const [playerBalance, setPlayerBalance] = useState(0);
  const [positions, setPositions] = useState([]);
  const [history, setHistory] = useState([]);
  const [opening, setOpening] = useState(false);
  const [walletRefreshKey, setWalletRefreshKey] = useState(0);
  const [treasury, setTreasury] = useState(null);

  const currentMarket = useMemo(
    () => markets.find((m) => m.market === selectedMarket) || null,
    [markets, selectedMarket]
  );

  // ---- fetchers ----
  const fetchMarkets = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/perps/markets`);
      const data = await r.json();
      setMarkets(data.markets || []);
    } catch {
      // silent — the UI will show empty state; toast on interaction only
    }
  }, []);

  const fetchTreasury = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/perps/treasury/public`);
      if (r.ok) setTreasury(await r.json());
    } catch { /* noop */ }
  }, []);

  const fetchPositions = useCallback(async () => {
    if (!isConnected) return;
    try {
      const r = await fetch(`${API}/api/perps/positions`, { headers: getAuthHeaders() });
      const d = await r.json();
      setPositions(d.positions || []);
    } catch { /* noop */ }
  }, [isConnected, getAuthHeaders]);

  const fetchHistory = useCallback(async () => {
    if (!isConnected) return;
    try {
      const r = await fetch(`${API}/api/perps/positions/history?limit=25`, { headers: getAuthHeaders() });
      const d = await r.json();
      setHistory(d.positions || []);
    } catch { /* noop */ }
  }, [isConnected, getAuthHeaders]);

  // Initial + polling: markets every 10s, positions every 5s so P&L stays live.
  useEffect(() => {
    fetchMarkets();
    fetchTreasury();
    const t = setInterval(fetchMarkets, 10000);
    const t2 = setInterval(fetchTreasury, 30000);
    return () => { clearInterval(t); clearInterval(t2); };
  }, [fetchMarkets, fetchTreasury]);

  useEffect(() => {
    if (!isConnected) return;
    fetchPositions();
    fetchHistory();
    const t = setInterval(() => {
      fetchPositions();
    }, 5000);
    return () => clearInterval(t);
  }, [isConnected, fetchPositions, fetchHistory]);

  // ---- derived preview ----
  const preview = useMemo(() => {
    if (!currentMarket) return null;
    const notionalMicro = Math.floor(Number(notionalStx || 0) * 1_000_000);
    if (notionalMicro <= 0) return null;
    const margin = Math.floor(notionalMicro / leverage);
    const spread = 0.0005;
    const entry = side === "long"
      ? currentMarket.price_usd * (1 + spread)
      : currentMarket.price_usd * (1 - spread);
    const band = 0.95 / leverage;
    const liq = side === "long" ? entry * (1 - band) : entry * (1 + band);
    return { notionalMicro, margin, entry, liq };
  }, [currentMarket, notionalStx, leverage, side]);

  const canAfford = preview && playerBalance >= preview.margin;

  // ---- actions ----
  async function openPosition() {
    if (!isConnected) {
      toast.error("Connect your wallet first");
      return;
    }
    if (!preview) {
      toast.error("Enter a valid size");
      return;
    }
    if (preview.notionalMicro < 10_000_000) {
      toast.error("Minimum position is 10 STXOSHI notional");
      return;
    }
    if (!canAfford) {
      toast.error("Insufficient STXOSHI balance for required margin");
      return;
    }
    setOpening(true);
    try {
      const r = await fetch(`${API}/api/perps/positions/open`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          market: selectedMarket,
          side,
          notional_stxoshi: preview.notionalMicro,
          leverage,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Failed to open position");
      } else {
        toast.success(`${side.toUpperCase()} ${selectedMarket}-PERP opened`);
        setWalletRefreshKey((k) => k + 1);
        fetchPositions();
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOpening(false);
    }
  }

  async function closePosition(id) {
    try {
      const r = await fetch(`${API}/api/perps/positions/${id}/close`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Failed to close");
        return;
      }
      const net = d.net_pnl_stxoshi || 0;
      const sign = net >= 0 ? "+" : "";
      toast.success(`Closed. Net P&L: ${sign}${fmtStxoshi(net)} ${unitLabel}`);
      setWalletRefreshKey((k) => k + 1);
      fetchPositions();
      fetchHistory();
    } catch (e) {
      toast.error(e.message);
    }
  }

  const [closingAll, setClosingAll] = useState(false);

  async function closeAllAndWithdraw() {
    if (positions.length === 0) {
      toast.info("No open positions to close.");
      return;
    }
    // Preview aggregate unrealized P&L so the user sees what they're locking in
    const previewNet = positions.reduce(
      (acc, p) => acc + (Number(p.unrealized_pnl_stxoshi) || 0),
      0,
    );
    const sign = previewNet >= 0 ? "+" : "";
    const ok = window.confirm(
      `Close all ${positions.length} open position${positions.length === 1 ? "" : "s"} at the current mark, then withdraw your entire User Wallet balance on-chain?\n\n` +
        `Est. realized P&L across all positions: ${sign}${fmtStxoshi(previewNet)} STXOSHI (based on current marks; final settlement uses spread & funding).\n\n` +
        `After close, your full wallet balance will be transferred to your Xverse address on Stacks mainnet.`,
    );
    if (!ok) return;
    setClosingAll(true);
    try {
      // Step 1: close every open position atomically on the backend
      const closeR = await fetch(`${API}/api/perps/positions/close-all`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const closeD = await closeR.json();
      if (!closeR.ok) {
        toast.error(closeD?.detail || "Close-all failed");
        return;
      }
      const closed = closeD.closed_count || 0;
      const failed = closeD.failed_count || 0;
      const net = closeD.total_net_pnl_stxoshi || 0;
      const netSign = net >= 0 ? "+" : "";
      if (closed > 0) {
        toast.success(
          `Closed ${closed} position${closed === 1 ? "" : "s"} · net ${netSign}${fmtStxoshi(net)} STXOSHI`,
          { duration: 6000 },
        );
      }
      if (failed > 0) {
        toast.warning(`${failed} position${failed === 1 ? "" : "s"} could not be closed — skipping withdraw.`);
        fetchPositions();
        fetchHistory();
        setWalletRefreshKey((k) => k + 1);
        return;
      }
      fetchPositions();
      fetchHistory();

      // Step 2: withdraw the entire wallet on-chain
      const balanceToWithdraw = closeD.wallet_balance_stxoshi || 0;
      if (balanceToWithdraw < 1_000_000) {
        toast.info("Wallet balance below 1 STXOSHI minimum — nothing to withdraw.");
        setWalletRefreshKey((k) => k + 1);
        return;
      }
      const wdR = await fetch(`${API}/api/flip-market/player-wallet/withdraw`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      const wdD = await wdR.json();
      if (!wdR.ok || !wdD?.success) {
        toast.error(wdD?.detail || "Withdraw failed — you can retry from the User Wallet card.");
        setWalletRefreshKey((k) => k + 1);
        return;
      }
      const amt = wdD?.data?.withdrawn_amount || 0;
      const txid = wdD?.data?.tx_id;
      toast.success(
        `Withdrew ${fmtStxoshi(amt)} STXOSHI on-chain`,
        txid
          ? {
              description: `TXID: ${txid.slice(0, 12)}…`,
              action: {
                label: "View",
                onClick: () =>
                  window.open(`https://explorer.hiro.so/txid/${txid}?chain=mainnet`, "_blank", "noopener"),
              },
              duration: 15000,
            }
          : undefined,
      );
      setWalletRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClosingAll(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Shared app header — brings the STXOSHI logo (links home) + user
          pulldown (with Perp Trading link) into consistency with other pages. */}
      <Header />

      {/* Perps-specific sub-header with title, tagline.
          Wallet connect lives in the shared Header above — don't duplicate. */}
      <div className="border-b border-border/40 px-4 py-3">
        <div className="min-w-0">
          <h1 className="font-heading font-bold tracking-wider text-base sm:text-lg truncate" data-testid="perps-title">
            STXOSHI PERPS
          </h1>
          <p className="text-[10px] text-muted-foreground -mt-0.5 truncate">
            Synthetic perpetuals · settles in STXOSHI
          </p>
        </div>
      </div>

      <main className="p-3 sm:p-4 md:p-6 max-w-5xl mx-auto space-y-4">
        {/* User Wallet — same card + Deposit/Withdraw flow as Flip Market */}
        {isConnected && (
          <UserWallet onBalanceChange={setPlayerBalance} refreshKey={walletRefreshKey} />
        )}
        {/* Market selector — 3-column grid on ALL viewports so it never
            horizontally scrolls off-screen on iPhone. */}
        <div className="grid grid-cols-3 gap-2" data-testid="perps-market-selector">
          {markets.map((m) => {
            const meta = MARKET_META[m.market] || { fmt: (v) => `$${v}` };
            const positive = m.change_24h_pct >= 0;
            const active = m.market === selectedMarket;
            return (
              <button
                key={m.market}
                onClick={() => setSelectedMarket(m.market)}
                className={`min-w-0 px-2 sm:px-4 py-2.5 rounded-lg border transition-all text-left ${
                  active
                    ? "border-orange-500 bg-orange-500/10"
                    : "border-border hover:border-orange-500/50"
                }`}
                data-testid={`perps-market-${m.market}`}
              >
                <div className="flex items-center gap-1 mb-0.5 min-w-0">
                  <span className="font-heading text-[11px] sm:text-sm font-bold truncate">
                    {m.symbol}
                  </span>
                  {active && <Zap className="w-3 h-3 text-orange-400 shrink-0" />}
                </div>
                <div className="font-mono text-[10px] sm:text-xs truncate">
                  {meta.fmt(m.price_usd)}
                </div>
                <div className={`text-[9px] sm:text-[10px] font-mono truncate ${positive ? "text-green-400" : "text-red-400"}`}>
                  {positive ? "+" : ""}
                  {m.change_24h_pct?.toFixed(2)}% 24h
                </div>
              </button>
            );
          })}
        </div>

        {/* House Treasury transparency — shows the STXOSHI that flows to the
            protocol treasury via liquidations (0% house edge on wins). */}
        {treasury && (
          <Card className="glass-card border-orange-500/30" data-testid="perps-treasury-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Landmark className="w-4 h-4 text-orange-400" />
                <span className="font-heading font-bold text-sm">House Treasury</span>
                <span className="text-[10px] text-muted-foreground">· liquidations feed the protocol treasury (0% edge on wins)</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div data-testid="treasury-liquidated-24h">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <Flame className="w-3 h-3 text-orange-400" /> 24h Liquidated
                  </div>
                  <div className="font-mono font-bold text-base sm:text-lg text-orange-400">
                    {fmtStxoshiCompact(treasury.liquidated_24h_stxoshi)}
                  </div>
                  <div className="text-[9px] text-muted-foreground">STXOSHI</div>
                </div>
                <div data-testid="treasury-lifetime-liquidated">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <ShieldAlert className="w-3 h-3 text-orange-400" /> Lifetime Liquidated
                  </div>
                  <div className="font-mono font-bold text-base sm:text-lg text-foreground">
                    {fmtStxoshiCompact(treasury.cumulative_liquidations_stxoshi)}
                  </div>
                  <div className="text-[9px] text-muted-foreground">STXOSHI</div>
                </div>
                <div className="col-span-2 sm:col-span-1" data-testid="treasury-swept">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <Landmark className="w-3 h-3 text-orange-400" /> Swept to Treasury
                  </div>
                  <div className="font-mono font-bold text-base sm:text-lg text-green-400">
                    {fmtStxoshiCompact(treasury.cumulative_swept_stxoshi)}
                  </div>
                  <div className="text-[9px] text-muted-foreground">STXOSHI</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {/* Order form */}
          <Card className="glass-card border-border" data-testid="perps-order-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold">Open position</h2>
                {currentMarket && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-mono">
                      Mark: {MARKET_META[currentMarket.market]?.fmt(currentMarket.price_usd)}
                    </span>
                    {typeof currentMarket.funding_rate_bps_per_hour === "number" && (
                      <span
                        title="Current funding rate (per hour). Longs pay when > 0, shorts pay when < 0. Applied at close."
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                          currentMarket.funding_rate_bps_per_hour > 0
                            ? "border-red-500/40 text-red-400"
                            : currentMarket.funding_rate_bps_per_hour < 0
                            ? "border-green-500/40 text-green-400"
                            : "border-border text-muted-foreground"
                        }`}
                        data-testid="perps-funding-rate"
                      >
                        F {currentMarket.funding_rate_bps_per_hour > 0 ? "+" : ""}
                        {currentMarket.funding_rate_bps_per_hour.toFixed(2)} bps/h
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Side toggle */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSide("long")}
                  className={`py-3 rounded-lg border font-heading font-bold transition-all ${
                    side === "long"
                      ? "border-green-500 bg-green-500/15 text-green-400"
                      : "border-border text-muted-foreground hover:border-green-500/50"
                  }`}
                  data-testid="perps-side-long"
                >
                  <TrendingUp className="w-4 h-4 inline mr-1" />
                  LONG
                </button>
                <button
                  onClick={() => setSide("short")}
                  className={`py-3 rounded-lg border font-heading font-bold transition-all ${
                    side === "short"
                      ? "border-red-500 bg-red-500/15 text-red-400"
                      : "border-border text-muted-foreground hover:border-red-500/50"
                  }`}
                  data-testid="perps-side-short"
                >
                  <TrendingDown className="w-4 h-4 inline mr-1" />
                  SHORT
                </button>
              </div>

              {/* Notional input */}
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">
                  Position size (STXOSHI notional)
                </label>
                <Input
                  type="number"
                  min="10"
                  step="10"
                  value={notionalStx}
                  onChange={(e) => setNotionalStx(e.target.value)}
                  placeholder="100"
                  className="mt-1 font-mono"
                  data-testid="perps-notional-input"
                />
                <div className="flex gap-1 mt-2 flex-wrap">
                  {[100, 500, 1000, 5000].map((v) => (
                    <button
                      key={v}
                      onClick={() => setNotionalStx(String(v))}
                      className="text-[10px] px-2 py-1 rounded border border-border hover:border-orange-500/50 font-mono"
                      data-testid={`perps-quick-notional-${v}`}
                    >
                      {v.toLocaleString()}
                    </button>
                  ))}
                  {playerBalance > 0 && (
                    <button
                      onClick={() =>
                        setNotionalStx(
                          String(Math.floor((playerBalance * leverage) / 1_000_000))
                        )
                      }
                      className="text-[10px] px-2 py-1 rounded border border-orange-500/50 text-orange-400 font-mono"
                      data-testid="perps-max-notional"
                    >
                      MAX
                    </button>
                  )}
                </div>
              </div>

              {/* Leverage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">
                    Leverage
                  </label>
                  <span className="font-heading font-bold text-orange-400" data-testid="perps-leverage-value">
                    {leverage}×
                  </span>
                </div>
                <Slider
                  min={1}
                  max={20}
                  step={1}
                  value={[leverage]}
                  onValueChange={(v) => setLeverage(v[0])}
                  data-testid="perps-leverage-slider"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-mono">
                  <span>1×</span><span>5×</span><span>10×</span><span>20×</span>
                </div>
              </div>

              {/* Preview */}
              {preview && currentMarket && (
                <div className="rounded-lg border border-border p-3 space-y-1 text-xs font-mono" data-testid="perps-preview">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Entry price</span>
                    <span>{MARKET_META[selectedMarket]?.fmt(preview.entry)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Required margin</span>
                    <span>{fmtStxoshi(preview.margin)} {unitLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" />
                      Liquidation
                    </span>
                    <span className={side === "long" ? "text-red-400" : "text-red-400"}>
                      {MARKET_META[selectedMarket]?.fmt(preview.liq)}
                    </span>
                  </div>
                </div>
              )}

              <Button
                className={`w-full font-heading font-bold h-11 ${
                  side === "long"
                    ? "bg-green-600 hover:bg-green-500 text-black"
                    : "bg-red-600 hover:bg-red-500 text-black"
                }`}
                onClick={openPosition}
                disabled={opening || !canAfford || !preview}
                data-testid="perps-open-btn"
              >
                {opening
                  ? "Opening…"
                  : !isConnected
                  ? "Connect Wallet"
                  : !canAfford
                  ? "Insufficient balance"
                  : `Open ${side.toUpperCase()} ${selectedMarket}-PERP`}
              </Button>

              {!canAfford && isConnected && (
                <p className="text-[11px] text-muted-foreground text-center">
                  Need {fmtStxoshi((preview?.margin || 0) - playerBalance)} more STXOSHI in your user site wallet.{" "}
                  <button
                    onClick={() => navigate("/flip-market")}
                    className="text-orange-400 underline"
                    data-testid="perps-goto-deposit"
                  >
                    Deposit
                  </button>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Open positions */}
          <Card className="glass-card border-border" data-testid="perps-positions-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h2 className="font-heading font-bold">Your positions</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs font-mono">
                    {positions.length} open
                  </Badge>
                  {isConnected && positions.length > 0 && (
                    <Button
                      size="sm"
                      onClick={closeAllAndWithdraw}
                      disabled={closingAll}
                      className="h-7 px-2 text-xs bg-orange-500 hover:bg-orange-600 text-black font-bold"
                      data-testid="perps-close-all-withdraw-btn"
                    >
                      {closingAll ? (
                        <><Clock className="w-3 h-3 mr-1 animate-spin" /> Closing…</>
                      ) : (
                        <>Close all & Withdraw</>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {!isConnected ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  Connect your wallet to view positions.
                </div>
              ) : positions.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground" data-testid="perps-no-positions">
                  <Info className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No open positions yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {positions.map((p) => {
                    const pnlPositive = (p.unrealized_pnl_stxoshi || 0) >= 0;
                    const meta = MARKET_META[p.market] || { fmt: (v) => `$${v}` };
                    return (
                      <div
                        key={p.id}
                        className="rounded-lg border border-border p-3 hover:border-orange-500/40 transition-colors"
                        data-testid={`perps-position-${p.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={`text-xs uppercase ${
                                p.side === "long"
                                  ? "border-green-500/40 text-green-400"
                                  : "border-red-500/40 text-red-400"
                              }`}
                            >
                              {p.side}
                            </Badge>
                            <span className="font-heading text-sm font-bold">{p.market}-PERP</span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {p.leverage}×
                            </span>
                          </div>
                          <span className="text-[10px] text-muted-foreground font-mono flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {relTime(p.opened_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
                          <div>
                            <p className="text-muted-foreground">Notional</p>
                            <p className="font-bold">{fmtStxoshi(p.notional_stxoshi)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Entry</p>
                            <p>{meta.fmt(p.entry_price_usd)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Mark</p>
                            <p>{meta.fmt(p.mark_price_usd)}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                              Unrealized P&L
                            </p>
                            <p
                              className={`font-mono font-bold text-lg ${
                                pnlPositive ? "text-green-400" : "text-red-400"
                              }`}
                              data-testid={`perps-pnl-${p.id}`}
                            >
                              {fmtSigned(p.unrealized_pnl_stxoshi)} {unitLabel}
                            </p>
                            {p.opened_at && (
                              <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`perps-opened-${p.id}`}>
                                Opened {new Date(p.opened_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => closePosition(p.id)}
                            data-testid={`perps-close-btn-${p.id}`}
                          >
                            Close
                          </Button>
                        </div>
                        {/* Health bar */}
                        <div className="mt-2 h-1 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              p.health_pct > 40 ? "bg-green-500" : p.health_pct > 15 ? "bg-orange-400" : "bg-red-500"
                            }`}
                            style={{ width: `${Math.max(2, Math.min(100, p.health_pct))}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Position history */}
        {history.length > 0 && (
          <Card className="glass-card border-border" data-testid="perps-history-card">
            <CardContent className="p-5">
              <h2 className="font-heading font-bold mb-3">Recent history</h2>
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {history.map((p) => {
                  const meta = MARKET_META[p.market] || { fmt: (v) => `$${v}` };
                  const positive = (p.realized_pnl_stxoshi || 0) >= 0;
                  const liquidated = p.status === "liquidated";
                  return (
                    <div
                      key={p.id}
                      className="flex items-center justify-between px-3 py-2 rounded border border-border/60 text-xs"
                      data-testid={`perps-history-${p.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <Badge
                          variant="outline"
                          className={`text-[10px] uppercase ${
                            p.side === "long" ? "border-green-500/40 text-green-400" : "border-red-500/40 text-red-400"
                          }`}
                        >
                          {p.side}
                        </Badge>
                        <span className="font-heading text-xs font-bold">{p.market}</span>
                        {liquidated && (
                          <Badge variant="outline" className="text-[10px] border-red-500 text-red-400 uppercase">
                            liquidated
                          </Badge>
                        )}
                        <span className="text-muted-foreground font-mono hidden sm:inline">
                          {meta.fmt(p.entry_price_usd)} → {meta.fmt(p.close_price_usd)}
                        </span>
                        {p.closed_at && (
                          <span
                            className="text-[10px] text-muted-foreground font-mono whitespace-nowrap"
                            title={new Date(p.closed_at).toLocaleString()}
                            data-testid={`perps-history-time-${p.id}`}
                          >
                            · {relTime(p.closed_at)}
                          </span>
                        )}
                      </div>
                      <span className={`font-mono font-bold ${positive ? "text-green-400" : "text-red-400"}`}>
                        {fmtSigned(p.realized_pnl_stxoshi)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer disclosure */}
        <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 text-[11px] text-muted-foreground leading-relaxed">
          <strong className="text-orange-400">How this works:</strong> STXOSHI Perps is a
          synthetic derivatives market that settles in STXOSHI. Prices come from live
          oracles (CoinGecko for BTC/STX, ALEX/VELAR for STXOSHI). Positions are
          collateralized by your STXOSHI user site wallet balance and can be liquidated
          when equity drops below {`5%`} of margin.
        </div>
      </main>
    </div>
  );
}
